import React, { useEffect, useState, useRef, useMemo } from "react";
import api from "@/services/api.js";
import toast from "react-hot-toast";
import { capture } from "@/instrument";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineX,
  HiOutlineInformationCircle,
  HiOutlineExclamationCircle,
  HiOutlineUser,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineIdentification,
  HiOutlineLocationMarker,
  HiOutlineXCircle,
} from "react-icons/hi";
import { TbPassword } from "react-icons/tb";
import logo from "@/assets/logo.png";
import { cleanPhone, formatBirthdate, formatINS, formatAddress } from "@/utils";
import { useLoading } from "@/context/LoadingContext.jsx";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import AbandonModal from "@/components/AbandonModal";
import DateInput from "@/components/DateInput";

const isPhoneValid = (phone) => {
  if (!phone) return true;
  return /^\+[1-9][0-9]{6,14}$/.test(cleanPhone(phone));
};

const isEmailValid = (email) => {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
};

const isINSValid = (ins) => {
  if (!ins) return true;
  const normalized = ins.toUpperCase();
  if (!/^[12]\d{4}(?:2[AB]|\d{2})\d{6}\d{2}$/.test(normalized)) return false;
  const first13Digits = normalized.substring(0, 13).replace("2A", "19").replace("2B", "18");
  const controlKey = parseInt(normalized.substring(13, 15), 10);
  const calculatedKey = 97 - (parseInt(first13Digits, 10) % 97);
  return calculatedKey === controlKey;
};

const isBirthdateValid = (date) => {
  if (!date) return true;
  return /^\d{4}-\d{2}-\d{2}$/.test(date);
};

const isAdult = (birthdate) => {
  if (!birthdate) return true;

  const today = new Date();
  const birthDate = new Date(birthdate);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age >= 18;
};

const floatingLabelInputClass = (hasError = false) => `
  block w-full px-3 py-2 bg-white border 
  ${hasError ? "border-red-500" : "border-gray-300"} 
  rounded-md focus:outline-none focus:ring-primary 
  focus:border-primary placeholder-transparent 
  peer disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100
`;

const floatingLabelClass = (hasError = false) => `
  absolute left-2 -top-2.5 px-1 text-xs transition-all 
  peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 
  peer-placeholder-shown:top-2 peer-focus:-top-2.5 peer-focus:text-xs 
  peer-focus:text-primary bg-white ${hasError ? "text-red-500" : "text-gray-600"}
  pointer-events-none
`;

const MANDATORY_FIELDS = ["email", "phone", "lastName", "firstName", "birthLastName", "birthdate", "birthplaceCode", "INS.value", "sexe", "address", "zipCode", "city", "country"];

const Identification = () => {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();

  const [otpModal, setOtpModal] = useState({ open: false, data: "", type: "" });
  const [otpSent, setOtpSent] = useState({ email: false, phone: false });
  const [patientSelection, setPatientSelection] = useState({ open: false, candidates: [] });
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [showAgeRestrictionModal, setShowAgeRestrictionModal] = useState(false);
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const [patient, setPatient] = useState({
    _id: "",
    blockedFields: [],
    firstName: "",
    lastName: "",
    birthLastName: "",
    birthplaceCode: "",
    birthplace: "",
    sexe: "",
    birthdate: "",
    address: "",
    zipCode: "",
    city: "",
    country: "",
    INS: {
      value: "",
      isQualified: false,
    },
    phone: "",
    email: "",
  });

  const [addressLabel, setAddressLabel] = useState("");
  const [birthplaceLabel, setBirthplaceLabel] = useState("");
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [laboratoryId, setLaboratoryId] = useState("");

  const emailRef = useRef(null);
  const phoneRef = useRef(null);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    const invalidEmail = !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(patient.email);
    if (otpSent.email || invalidEmail || patient.isKnownPatient === true) return;

    sendOtp(patient.email, "email");
  }, [patient.email]);

  useEffect(() => {
    const cleanedPhone = cleanPhone(patient.phone);
    const invalidFrenchPhone = !/^\+(?:33)[0-9]{9}$/.test(cleanedPhone);
    if (otpSent.phone || invalidFrenchPhone || patient.isKnownPatient === true) return;

    sendOtp(cleanedPhone, "phone");
  }, [patient.phone]);

  const getData = async () => {
    try {
      startLoading();
      const res = await api.get(`/admission`);
      if (!res.ok) throw res;

      if (res.data?.status !== "DRAFT") {
        navigate("/");
        return stopLoading();
      }

      setPatient(res.data.patient);
      if (res.data.patient.address) {
        setAddressLabel(`${res.data.patient.address} ${res.data.patient.zipCode} ${res.data.patient.city}`);
      }
      if (res.data.patient.birthplaceCode) {
        setBirthplaceLabel(res.data.patient.birthplace || res.data.patient.birthplaceCode);
      }

      setLaboratoryId(res.data.laboratoryId);
    } catch (e) {
      if (e?.code === "UNAUTHORIZED") return navigate("/");
      capture(e);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      stopLoading();
    }
  };

  const sendOtp = async (value, type) => {
    try {
      let { _id, __v, birthLastName, ...updatedPatient } = patient;
      updatedPatient.phone = cleanPhone(updatedPatient.phone);
      const missingFields = Object.keys(updatedPatient).some((key) => !updatedPatient[key]);
      if (missingFields === false) return;

      const res = await api.post(`/patient/request-otp`, { type, value, laboratoryId });
      if (!res.ok) return;
      setOtpModal({ open: true, data: value, type: type });
      setOtpSent((prev) => ({ ...prev, [type]: true }));
    } catch (e) {
      if (e?.code === "PATIENT_NOT_FOUND") return;
      capture(e);
    }
  };

  const signin = async (otp) => {
    try {
      startLoading();

      let value = null;
      if (otpModal.type === "email") value = patient.email;
      if (otpModal.type === "phone") value = cleanPhone(patient.phone);

      const res = await api.post(`/patient/verify-otp`, { value, type: otpModal.type, otp, laboratoryId });

      if (!res.ok && res?.message) {
        toast.error(res.message);
        return stopLoading();
      }

      if (!res.ok) throw res;

      if (res.data?.candidates && res.data.candidates.length > 0) {
        setOtpModal({ open: false, data: "", type: "" });
        setPatientSelection({ open: true, candidates: res.data.candidates, selectionToken: res.data.selectionToken });
        stopLoading();
        return;
      }

      setOtpModal({ open: false, data: "", type: "" });
      setOtpSent({ email: false, phone: false });
      stopLoading();
    } catch (e) {
      if (e?.code === "PATIENT_NOT_FOUND" || e?.code === "OTP_INVALID") {
        toast.error("Le code de vérification est incorrect. Veuillez réessayer.");
        return stopLoading();
      }

      capture(e);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      stopLoading();
    }
  };

  const linkPatientToAdmission = async () => {
    try {
      const res = await api.put(`/admission/link-patient`);

      if (!res.ok && res?.message) {
        toast.error(res.message);
        return;
      }

      if (!res.ok) throw res;

      await getData();
    } catch (e) {
      capture(e);
      toast.error("Une erreur est survenue lors de la liaison du patient.");
    }
  };

  const finalizePatientSelection = async (patientId) => {
    try {
      startLoading();

      const res = await api.post(`/patient/select-account`, { selectedPatientId: patientId, selectionToken: patientSelection.selectionToken });

      if (!res.ok && res?.message) {
        toast.error(res.message);
        return stopLoading();
      }

      if (!res.ok) throw res;

      sessionStorage.setItem("patientToken", res.data.token);
      localStorage.setItem("rememberMe", res.data.rememberMe);

      setPatientSelection({ open: false, candidates: [] });
      await linkPatientToAdmission();
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors de la sélection des informations personnelles.");
    } finally {
      stopLoading();
    }
  };

  const handleSkipPatientSelection = () => {
    setPatientSelection({ open: false, candidates: [] });
    setOtpSent({ email: true, phone: true });
  };

  async function onSave() {
    setSubmissionAttempted(true);

    const missing = MANDATORY_FIELDS.filter((field) => isFieldMissing(field, true));

    if (missing.length > 0) {
      const elements = document.querySelectorAll(missing.map((field) => `[id="${field}"]`).join(","));

      if (elements.length > 0) {
        elements[0].scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => elements[0].focus(), 300);
      }
      toast.error(`${missing.length} champ${missing.length > 1 ? "s" : ""} obligatoire${missing.length > 1 ? "s" : ""} manquant${missing.length > 1 ? "s" : ""}`);
      return;
    }

    if (patient.INS?.value && !isINSValid(patient.INS?.value)) {
      const insElement = document.getElementById("INS.value");
      if (insElement) {
        insElement.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => insElement.focus(), 300);
      }
      toast.error("Le format du numéro de sécurité sociale est invalide");
      return;
    }

    if (!isAdult(patient.birthdate)) {
      setShowAgeRestrictionModal(true);
      return;
    }

    try {
      startLoading();
      const res = await api.put(`/admission/identification`, { patient: { ...patient, phone: cleanPhone(patient.phone) } });

      if (!res.ok && res?.message) {
        toast.error(res.message);
        return stopLoading();
      }

      if (!res.ok) throw res;

      navigate(`/question`);
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors de la sauvegarde des informations.");
    } finally {
      stopLoading();
    }
  }

  const isFieldMissing = (fieldName, ignoreSubmissionAttempted = false) => {
    if (submissionAttempted === false && ignoreSubmissionAttempted === false) return false;

    if (fieldName?.includes(".")) {
      const [key, subKey] = fieldName.split(".");
      return !patient[key]?.[subKey];
    }
    return !patient[fieldName];
  };

  return (
    <div className="container mx-auto min-w-full min-h-screen flex flex-col font-sans text-center p-5 pb-44">
      <div className="mt-5">
        <img src={logo} alt="Logo" className="mx-auto h-12" />
      </div>
      <div className="container mx-auto flex justify-center items-center font-sans flex-col grow">
        {patient.isKnownPatient === true && (
          <div className="w-full mt-8 rounded-lg p-4 border border-primary flex items-start">
            <HiOutlineInformationCircle className="text-primary flex-shrink-0 w-4 h-4 mt-0.5 mr-2" />
            <div>
              <p className="text-sm text-primary">
                Pour modifier vos informations personnelles pré-remplies (sauf adresse), merci de vous adresser au secrétariat lors de votre visite.
              </p>
            </div>
          </div>
        )}
        <div className="mt-4 w-full text-left rounded-lg">
          <p className="font-bold text-lg text-primary">Contact</p>

          <div className="grid md:grid-cols-2 gap-x-4 gap-y-2 mt-2 ">
            <div className="relative">
              <div className="relative flex items-center">
                <input
                  disabled={patient.blockedFields?.includes("phone") === true}
                  ref={phoneRef}
                  id="phone"
                  type="text"
                  value={patient.phone}
                  onChange={(e) => setPatient({ ...patient, phone: e.target.value })}
                  placeholder="Numéro de téléphone"
                  className={floatingLabelInputClass(!isPhoneValid(patient.phone) || isFieldMissing("phone"))}
                />
                <label htmlFor="phone" className={floatingLabelClass(!isPhoneValid(patient.phone) || isFieldMissing("phone"))}>
                  Numéro de téléphone
                </label>
                {otpSent && otpModal.type === "phone" && (
                  <button
                    className="absolute inset-y-0 right-0 px-3 bg-primary-200 rounded-r-md text-primary border border-primary flex items-center"
                    onClick={() => setOtpModal({ ...otpModal, open: true })}
                  >
                    <TbPassword className="w-6 h-6" />
                  </button>
                )}
              </div>

              <p className={`text-red-500 text-xs ${(patient.phone && !isPhoneValid(patient.phone)) || isFieldMissing("phone") ? "visible" : "invisible"}`}>
                {isFieldMissing("phone") ? "Ce champ est obligatoire" : "Format de téléphone invalide"}
              </p>
            </div>
            <div className="relative">
              <div className="relative flex items-center">
                <input
                  disabled={patient.blockedFields?.includes("email") === true}
                  ref={emailRef}
                  id="email"
                  type="text"
                  inputMode="email"
                  value={patient.email}
                  onChange={(e) => setPatient({ ...patient, email: e.target.value })}
                  placeholder="Adresse Email"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  className={floatingLabelInputClass(!isEmailValid(patient.email) || isFieldMissing("email"))}
                />
                <label htmlFor="email" className={floatingLabelClass(!isEmailValid(patient.email) || isFieldMissing("email"))}>
                  Adresse Email
                </label>
                {otpSent && otpModal.type === "email" && (
                  <button
                    className="absolute inset-y-0 right-0 px-3 bg-primary-200 rounded-r-md text-primary border border-primary flex items-center"
                    onClick={() => setOtpModal({ ...otpModal, open: true })}
                  >
                    <TbPassword className="w-6 h-6" />
                  </button>
                )}
              </div>
              <p className={`text-red-500 text-xs ${(patient.email && !isEmailValid(patient.email)) || isFieldMissing("email") ? "visible" : "invisible"}`}>
                {isFieldMissing("email") ? "Ce champ est obligatoire" : "Format d'email invalide"}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-2 w-full text-left rounded-lg">
          <p className="font-bold text-lg text-primary">Identité</p>

          <div className="grid md:grid-cols-2 gap-x-4 gap-y-2 mt-2">
            <div className="relative">
              <input
                disabled={patient.blockedFields?.includes("lastName") === true}
                id="lastName"
                type="text"
                value={patient.lastName}
                onChange={(e) => setPatient({ ...patient, lastName: e.target.value })}
                placeholder="Nom"
                className={floatingLabelInputClass(isFieldMissing("lastName"))}
              />
              <label htmlFor="lastName" className={floatingLabelClass(isFieldMissing("lastName"))}>
                Nom
              </label>
              <p className={`text-red-500 text-xs ${isFieldMissing("lastName") ? "visible" : "invisible"}`}>Ce champ est obligatoire</p>
            </div>
            <div className="relative">
              <input
                disabled={patient.blockedFields?.includes("firstName") === true}
                id="firstName"
                type="text"
                value={patient.firstName}
                onChange={(e) => setPatient({ ...patient, firstName: e.target.value })}
                placeholder="Prénom"
                className={floatingLabelInputClass(isFieldMissing("firstName"))}
              />
              <label htmlFor="firstName" className={floatingLabelClass(isFieldMissing("firstName"))}>
                Prénom
              </label>
              <p className={`text-red-500 text-xs ${isFieldMissing("firstName") ? "visible" : "invisible"}`}>Ce champ est obligatoire</p>
            </div>
            <div className="relative">
              <select
                disabled={patient.blockedFields?.includes("sexe") === true}
                id="sexe"
                value={patient.sexe}
                onChange={(e) => setPatient({ ...patient, sexe: e.target.value })}
                placeholder="Sexe"
                className={`${floatingLabelInputClass(isFieldMissing("sexe"))} h-10 w-full min-w-full box-border appearance-none`}
              >
                <option value="" disabled>
                  Sexe
                </option>
                <option value="M">Homme</option>
                <option value="F">Femme</option>
              </select>
              <label htmlFor="sexe" className={floatingLabelClass(isFieldMissing("sexe"))}>
                Sexe
              </label>
              <p className={`text-red-500 text-xs ${isFieldMissing("sexe") ? "visible" : "invisible"}`}>Ce champ est obligatoire</p>
            </div>

            <div className="relative">
              <DateInput
                disabled={patient.blockedFields?.includes("birthdate") === true}
                id="birthdate"
                value={patient.birthdate}
                onChange={(date) => setPatient({ ...patient, birthdate: date })}
                placeholder="Date de naissance"
                className="h-[40px]"
                isInError={
                  isFieldMissing("birthdate") ||
                  (patient.birthdate && !isBirthdateValid(patient.birthdate)) ||
                  (patient.birthdate && isBirthdateValid(patient.birthdate) && !isAdult(patient.birthdate))
                }
              />
              <p
                className={`text-red-500 text-xs ${
                  (patient.birthdate && !isBirthdateValid(patient.birthdate)) ||
                  isFieldMissing("birthdate") ||
                  (patient.birthdate && isBirthdateValid(patient.birthdate) && !isAdult(patient.birthdate))
                    ? "visible"
                    : "invisible"
                }`}
              >
                {isFieldMissing("birthdate")
                  ? "Ce champ est obligatoire"
                  : patient.birthdate && !isBirthdateValid(patient.birthdate)
                  ? "Format de date invalide"
                  : "Vous devez avoir au moins 18 ans pour utiliser ce service"}
              </p>
            </div>
            <div className="relative">
              <input
                disabled={patient.blockedFields?.includes("birthLastName") === true}
                id="birthLastName"
                type="text"
                value={patient.birthLastName}
                onChange={(e) => setPatient({ ...patient, birthLastName: e.target.value })}
                placeholder="Nom de naissance"
                className={floatingLabelInputClass(isFieldMissing("birthLastName"))}
              />
              <label htmlFor="birthLastName" className={floatingLabelClass(isFieldMissing("birthLastName"))}>
                Nom de naissance
              </label>
              <p className={`text-red-500 text-xs ${isFieldMissing("birthLastName") ? "visible" : "invisible"}`}>Ce champ est obligatoire</p>
            </div>
            <div className="relative">
              <AddressAutocomplete
                id="birthplaceCode"
                type="municipality"
                initialValue={birthplaceLabel}
                onChange={() => setPatient((prev) => ({ ...prev, birthplaceCode: "", birthplace: "" }))}
                onSelect={(suggestion) => {
                  const cityCode = suggestion.properties.citycode;
                  const cityLabel = suggestion.properties.label;
                  setPatient((prev) => ({
                    ...prev,
                    birthplaceCode: cityCode,
                    birthplace: cityLabel,
                  }));
                  setBirthplaceLabel(cityLabel);
                }}
                onNotFound={() => {
                  setBirthplaceLabel("Inconnu");
                  setPatient((prev) => ({ ...prev, birthplaceCode: "99999", birthplace: "Inconnu" }));
                }}
                onNotFoundMessage="Lieu de naissance introuvable ? Cliquez ici pour continuer"
                placeholder="Lieu de naissance"
                disabled={patient.blockedFields?.includes("birthplaceCode") === true}
                hasError={isFieldMissing("birthplaceCode")}
                label="Lieu de naissance"
                isRequired={true}
                errorMessage="Ce champ est obligatoire"
              />
            </div>
            <div className="relative md:col-span-2">
              <input
                disabled={patient.blockedFields?.includes("INS") === true}
                id="INS.value"
                type="number"
                inputMode="numeric"
                value={patient.INS?.value}
                onChange={(e) => setPatient({ ...patient, INS: { value: e.target.value, isQualified: false } })}
                placeholder="№ de sécurité sociale (15 chiffres)"
                className={floatingLabelInputClass(!isINSValid(patient.INS?.value) || isFieldMissing("INS.value"))}
              />
              <label htmlFor="INS" className={floatingLabelClass(!isINSValid(patient.INS?.value) || isFieldMissing("INS.value"))}>
                № de sécurité sociale (15 chiffres)
              </label>
              <p className={`text-red-500 text-xs ${(patient.INS?.value && !isINSValid(patient.INS?.value)) || isFieldMissing("INS.value") ? "visible" : "invisible"}`}>
                {isFieldMissing("INS.value")
                  ? "Ce champ est obligatoire"
                  : 13 <= patient.INS?.value?.length && patient.INS?.value?.length < 15
                  ? "Les deux derniers chiffres (clé de contrôle) sont manquants"
                  : "Le format du numéro de sécurité sociale est invalide"}
              </p>
            </div>

            {/* Address section with validation */}
            <div className="md:col-span-2">
              {!isManualAddress ? (
                <div>
                  <AddressAutocomplete
                    id="address"
                    type="housenumber"
                    initialValue={addressLabel}
                    onChange={() => setPatient((prev) => ({ ...prev, address: "", zipCode: "", city: "", country: "" }))}
                    onSelect={(suggestion) => {
                      setPatient((prev) => ({
                        ...prev,
                        address: suggestion.properties.name,
                        zipCode: suggestion.properties.postcode,
                        city: suggestion.properties.city,
                        country: "France",
                      }));
                      setAddressLabel(suggestion.properties.label);
                    }}
                    placeholder="Adresse"
                    hasError={isFieldMissing("address") || isFieldMissing("zipCode") || isFieldMissing("city") || isFieldMissing("country")}
                    label="Adresse"
                    isRequired={true}
                    errorMessage="Veuillez sélectionner une adresse complète"
                    onNotFoundMessage="Adresse introuvable ? Cliquez ici pour continuer"
                    onNotFound={() => setIsManualAddress(true)}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-y-2">
                  <div className="relative">
                    <input
                      id="address"
                      type="text"
                      value={patient.address}
                      onChange={(e) => setPatient({ ...patient, address: e.target.value })}
                      placeholder="Numéro et rue"
                      className={floatingLabelInputClass(isFieldMissing("address"))}
                    />
                    <label htmlFor="address" className={floatingLabelClass(isFieldMissing("address"))}>
                      Numéro et rue
                    </label>
                    <p className={`text-red-500 text-xs ${isFieldMissing("address") ? "visible" : "invisible"}`}>Ce champ est obligatoire</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-x-4 gap-y-2">
                    <div className="relative">
                      <input
                        id="zipCode"
                        type="text"
                        value={patient.zipCode}
                        onChange={(e) => setPatient({ ...patient, zipCode: e.target.value })}
                        placeholder="Code postal"
                        className={floatingLabelInputClass(isFieldMissing("zipCode"))}
                      />
                      <label htmlFor="zipCode" className={floatingLabelClass(isFieldMissing("zipCode"))}>
                        Code postal
                      </label>
                      <p className={`text-red-500 text-xs ${isFieldMissing("zipCode") ? "visible" : "invisible"}`}>Ce champ est obligatoire</p>
                    </div>
                    <div className="relative">
                      <input
                        id="city"
                        type="text"
                        value={patient.city}
                        onChange={(e) => setPatient({ ...patient, city: e.target.value })}
                        placeholder="Ville"
                        className={floatingLabelInputClass(isFieldMissing("city"))}
                      />
                      <label htmlFor="city" className={floatingLabelClass(isFieldMissing("city"))}>
                        Ville
                      </label>
                      <p className={`text-red-500 text-xs ${isFieldMissing("city") ? "visible" : "invisible"}`}>Ce champ est obligatoire</p>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      id="country"
                      type="text"
                      value={patient.country}
                      onChange={(e) => setPatient({ ...patient, country: e.target.value })}
                      placeholder="Pays"
                      className={floatingLabelInputClass(isFieldMissing("country"))}
                    />
                    <label htmlFor="country" className={floatingLabelClass(isFieldMissing("country"))}>
                      Pays
                    </label>
                    <p className={`text-red-500 text-xs ${isFieldMissing("country") ? "visible" : "invisible"}`}>Ce champ est obligatoire</p>
                  </div>
                  <div className="flex justify-end items-center">
                    <button type="button" className="text-xs text-primary hover:underline" onClick={() => setIsManualAddress(false)}>
                      Revenir à la recherche
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {otpModal.open && (
          <OTPModal
            otpData={otpModal.data}
            otpType={otpModal.type}
            onComplete={(otp) => signin(otp)}
            onClose={() => setOtpModal({ ...otpModal, open: false })}
            anchorEl={otpModal.type === "email" ? emailRef.current : phoneRef.current}
          />
        )}

        {patientSelection.open && <PatientSelectionModal candidates={patientSelection.candidates} onClose={handleSkipPatientSelection} onSelect={finalizePatientSelection} />}

        {showAgeRestrictionModal && <AgeRestrictionModal onClose={() => setShowAgeRestrictionModal(false)} />}

        {showAbandonModal && <AbandonModal onClose={() => setShowAbandonModal(false)} />}
      </div>
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-t border-gray-200 shadow-lg px-5 py-4">
        <div className="container mx-auto max-w-2xl">
          <p className="text-sm text-gray-500 mb-2 text-center">Veuillez remplir tous les champs obligatoires pour continuer</p>
          <button className="w-full px-4 py-2 rounded-md bg-primary text-white disabled:bg-gray-400 disabled:cursor-not-allowed" onClick={onSave}>
            Continuer
          </button>
          <button
            type="button"
            onClick={() => setShowAbandonModal(true)}
            className="w-full mt-1 px-4 py-2 rounded-md text-gray-500 hover:text-red-500 transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <HiOutlineXCircle className="w-4 h-4" />
            Abandonner l'enregistrement
          </button>
        </div>
      </div>
    </div>
  );
};

const PatientSelectionModal = ({ candidates, onSelect, onClose }) => {
  const isMobile = useMemo(() => window.innerWidth < 768, []);
  const modalRef = useRef(null);
  const scrollableRef = useRef(null);

  useEffect(() => {
    const modalElement = modalRef.current;
    if (isMobile === false || modalElement === null) {
      return;
    }

    const touchStartYRef = { value: 0 };
    let isSwipeGesture = false;

    const handleTouchStart = (event) => {
      touchStartYRef.value = event.touches[0].clientY;

      const scrollableElement = scrollableRef.current;
      const isInsideScrollable = scrollableElement && scrollableElement.contains(event.target);

      isSwipeGesture = !isInsideScrollable;

      if (isSwipeGesture) modalElement.style.transition = "none";
    };

    const handleTouchMove = (event) => {
      if (isSwipeGesture === false) return;

      const touchCurrentY = event.touches[0].clientY;
      const deltaY = touchCurrentY - touchStartYRef.value;

      if (deltaY > 0) {
        event.preventDefault();
        modalElement.style.transform = `translateY(${deltaY}px)`;
      }
    };

    const handleTouchEnd = (event) => {
      if (isSwipeGesture === false) return;

      const touchEndY = event.changedTouches[0].clientY;
      const deltaY = touchEndY - touchStartYRef.value;
      const closeThreshold = 100;

      modalElement.style.transition = "transform 0.3s ease-out";

      if (deltaY > closeThreshold) {
        modalElement.style.transform = `translateY(100%)`;
        setTimeout(() => {
          onClose();
        }, 300);
      } else {
        modalElement.style.transform = "translateY(0)";
      }
    };

    modalElement.addEventListener("touchstart", handleTouchStart);
    modalElement.addEventListener("touchmove", handleTouchMove, { passive: false });
    modalElement.addEventListener("touchend", handleTouchEnd);

    return () => {
      modalElement.removeEventListener("touchstart", handleTouchStart);
      modalElement.removeEventListener("touchmove", handleTouchMove);
      modalElement.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, onClose]);

  const getCandidateMeta = (candidate) => {
    const { _id, firstName, lastName, birthdate, birthLastName, sexe, INS, address, zipCode, city, country, email, phone } = candidate;

    return {
      id: _id,
      firstName,
      lastName,
      birthLastName,
      displayName: [firstName, lastName].filter(Boolean).join(" ") || "Identité personnelle",
      initials: `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?",
      formattedBirthdate: formatBirthdate(birthdate),
      sexe,
      formattedINS: formatINS(INS?.value),
      formattedAddress: formatAddress({ address, zipCode, city, country }),
      email,
      phone: phone ? cleanPhone(phone) : null,
    };
  };

  const CandidateDetails = ({ meta, className = "" }) => (
    <div className={`space-y-2.5 text-[13px] text-gray-600 ${className}`}>
      {meta.formattedBirthdate && (
        <div className="flex items-center gap-2.5">
          <HiOutlineIdentification className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{meta.formattedBirthdate}</span>
          {meta.sexe && <span className="text-gray-400">• {meta.sexe}</span>}
        </div>
      )}
      {meta.formattedINS && (
        <div className="flex items-center gap-2.5">
          <HiOutlineUser className="h-4 w-4 text-gray-400 shrink-0" />
          <span className="font-mono">{meta.formattedINS}</span>
        </div>
      )}
      {meta.formattedAddress && (
        <div className="flex items-start gap-2.5">
          <HiOutlineLocationMarker className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
          <span className="leading-relaxed">{meta.formattedAddress}</span>
        </div>
      )}
      {meta.email && (
        <div className="flex items-center gap-2.5">
          <HiOutlineMail className="h-4 w-4 text-gray-400 shrink-0" />
          <span className="truncate">{meta.email}</span>
        </div>
      )}
      {meta.phone && (
        <div className="flex items-center gap-2.5">
          <HiOutlinePhone className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{meta.phone}</span>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px] transition-opacity duration-200" onClick={onClose} />
        <div ref={modalRef} className="absolute inset-x-0 bottom-0 z-50" style={{ transition: "transform 0.3s ease-out", touchAction: "none" }}>
          <div className="relative bg-white rounded-t-3xl shadow-2xl px-5 pt-4 pb-6 max-h-[85vh]">
            <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-300" />

            <div className="mt-4 text-left">
              <h2 className="text-lg font-semibold text-gray-900">Sélectionnez vos informations personnelles</h2>
              <p className="mt-2 text-sm text-gray-500">
                {candidates.length === 1
                  ? "Un profil correspond à vos informations. Vous pouvez l'utiliser pour préremplir votre demande ou continuer sans."
                  : "Plusieurs profils correspondent à vos informations. Choisissez celui qui reflète le mieux votre identité."}
              </p>
            </div>

            <div ref={scrollableRef} className="mt-6 space-y-4 overflow-y-auto pr-1 max-h-[60vh] pb-4">
              {candidates.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                    <HiOutlineUser className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">Aucune identité personnelle n'a été trouvée avec ces informations.</p>
                </div>
              ) : (
                <>
                  {candidates.map((candidate) => {
                    const meta = getCandidateMeta(candidate);
                    const showBirthName = meta.birthLastName && meta.birthLastName !== meta.lastName;

                    return (
                      <button
                        key={meta.id}
                        type="button"
                        onClick={() => onSelect(meta.id)}
                        className="group relative w-full rounded-2xl border border-gray-200/80 bg-white/90 px-4 py-4 text-left shadow-sm hover:border-primary"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 text-white text-base font-semibold shadow-sm">
                            {meta.initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-base font-semibold text-gray-900 group-hover:text-primary transition-colors">{meta.displayName}</p>
                            {showBirthName && <p className="text-xs text-gray-500">Né(e) {meta.birthLastName}</p>}
                          </div>
                        </div>

                        <CandidateDetails meta={meta} className="mt-4" />
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={onClose}
                    className="group relative w-full rounded-2xl border-2 border-dashed border-gray-300 bg-white px-4 py-5 text-left hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 active:scale-[0.98]"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                        <HiOutlineUser className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-gray-900 group-hover:text-primary transition-colors">Remplir mes informations manuellement</p>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">Je préfère saisir mes informations moi-même plutôt que d'utiliser un profil existant</p>
                      </div>
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-gray-100 bg-white shadow-xl transition-all duration-200">
          <div className="relative px-8 py-6">
            <button onClick={onClose} className="absolute top-6 right-6 inline-flex h-9 w-9 items-center justify-center text-gray-500 hover:text-primary" aria-label="Fermer">
              <HiOutlineX className="w-5 h-5" />
            </button>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900">Sélectionnez vos informations personnelles</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-gray-500">
                {candidates.length === 1
                  ? "Un profil correspond à vos informations. Vous pouvez l'utiliser pour préremplir votre demande ou continuer sans utiliser ces informations."
                  : "Plusieurs profils correspondent à vos informations. Choisissez les informations personnelles appropriées pour continuer."}
              </p>
            </div>

            {candidates.length === 0 ? (
              <div className="py-12 text-center">
                <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <HiOutlineUser className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-gray-500">Aucune identité personnelle disponible avec ces informations.</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {candidates.map((candidate) => {
                  const meta = getCandidateMeta(candidate);
                  const showBirthName = meta.birthLastName && meta.birthLastName !== meta.lastName;

                  return (
                    <button
                      key={meta.id}
                      type="button"
                      onClick={() => onSelect(meta.id)}
                      className="group relative rounded-2xl border border-gray-200 bg-white p-5 text-left hover:border-primary"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 text-white text-base font-semibold shadow-sm">
                          {meta.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-base font-semibold text-gray-900 transition-colors group-hover:text-primary">{meta.displayName}</p>
                          {showBirthName && <p className="text-xs text-gray-500 mt-0.5">Né(e) {meta.birthLastName}</p>}
                        </div>
                      </div>

                      <CandidateDetails meta={meta} className="mt-5" />

                      <div className="pointer-events-none absolute right-5 top-5 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white shadow-sm">
                          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={onClose}
                  className="group relative rounded-2xl border-2 border-dashed border-gray-300 bg-white p-5 text-left hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 active:scale-[0.98]"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                      <HiOutlineUser className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-semibold text-gray-900 transition-colors group-hover:text-primary">Remplir mes informations manuellement</p>
                      <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">Je préfère saisir mes informations moi-même plutôt que d'utiliser un profil existant</p>
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const OTPModal = ({ otpData, otpType, onComplete, onClose, anchorEl }) => {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef([]);
  const modalRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    if (isMobile === false) return;
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
  }, []);

  useEffect(() => {
    if (anchorEl && !isMobile) {
      const rect = anchorEl.getBoundingClientRect();
      const modalWidth = 320; // Largeur de la modal

      // Calcul pour centrer la modal par rapport au champ
      const centerOfField = rect.left + rect.width / 2;
      const leftPosition = centerOfField - modalWidth / 2 + window.scrollX;

      const finalLeftPosition = Math.max(16, Math.min(leftPosition, window.innerWidth - modalWidth - 16));

      setPosition({ top: rect.bottom + window.scrollY + 10, left: finalLeftPosition });
    }
  }, [anchorEl, isMobile]);

  useEffect(() => {
    const modalElement = modalRef.current;
    if (isMobile === false || modalElement === null) {
      return;
    }

    const touchStartYRef = { value: 0 };

    const handleTouchStart = (event) => {
      touchStartYRef.value = event.touches[0].clientY;
      modalElement.style.transition = "none";
    };

    const handleTouchMove = (event) => {
      const touchCurrentY = event.touches[0].clientY;
      const deltaY = touchCurrentY - touchStartYRef.value;

      if (deltaY > 0) {
        event.preventDefault();
        modalElement.style.transform = `translateY(${deltaY}px)`;
      }
    };

    const handleTouchEnd = (event) => {
      const touchEndY = event.changedTouches[0].clientY;
      const deltaY = touchEndY - touchStartYRef.value;
      const closeThreshold = 100;

      modalElement.style.transition = "transform 0.3s ease-out";

      if (deltaY > closeThreshold) {
        modalElement.style.transform = `translateY(100%)`;
        setTimeout(() => {
          onClose();
        }, 300);
      } else {
        modalElement.style.transform = "translateY(0)";
      }
    };

    modalElement.addEventListener("touchstart", handleTouchStart);
    modalElement.addEventListener("touchmove", handleTouchMove, { passive: false });
    modalElement.addEventListener("touchend", handleTouchEnd);

    return () => {
      modalElement.removeEventListener("touchstart", handleTouchStart);
      modalElement.removeEventListener("touchmove", handleTouchMove);
      modalElement.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, onClose]);

  const handleSubmit = async (otp) => {
    setIsLoading(true);
    await onComplete(otp);
    setIsLoading(false);
  };

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];

    // Take only the last character if multiple digits are pasted
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Move to next input if value is entered
    if (value && index < otp.length - 1) {
      inputRefs.current[index + 1].focus();
    }

    if (newOtp.every((digit) => digit !== "")) {
      handleSubmit(newOtp.join(""));
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, otp.length);

    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split("").forEach((value, index) => {
      if (index < otp.length) {
        newOtp[index] = value;
      }
    });

    setOtp(newOtp);

    const lastFilledIndex = Math.min(pastedData.length - 1, otp.length - 1);
    inputRefs.current[lastFilledIndex].focus();

    if (newOtp.every((digit) => digit !== "")) {
      handleSubmit(newOtp.join(""));
    }
  };

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        <div ref={modalRef} className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up" style={{ transition: "transform 0.3s ease-out", touchAction: "none" }}>
          <div className="bg-white rounded-t-2xl p-6 w-full shadow-xl">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />

            <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-500">
              <HiOutlineX className="w-4 h-4" />
            </button>

            <div className="text-center">
              <h1 className="text-lg font-bold">Confirmer que c'est vous</h1>
              <p className="text-sm mt-2">
                Entrer le code reçu par {otpType === "email" ? "e-mail" : "SMS"} ({otpData}) pour préremplir votre demande de manière sécurisée
                <br />
                {otpType === "email" && <span className="text-xs text-red-500"> (L'email peut être dans vos spams)</span>}
              </p>

              {isLoading ? (
                <div className="flex gap-2 justify-center mt-4">
                  <p>Chargement...</p>
                </div>
              ) : (
                <div className="flex gap-2 justify-center mt-4">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      autoFocus={index === 0}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      ref={(el) => (inputRefs.current[index] = el)}
                      onChange={(e) => handleChange(e, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={handlePaste}
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      className="w-12 h-12 text-center border border-gray-300 rounded-lg text-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  ))}
                </div>
              )}
              <p className="text-xs mt-4">Vous pourrez vérifier vos informations à la prochaine étape</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="absolute z-50" style={{ top: `${position.top}px`, left: `${position.left}px` }}>
        <div className="relative bg-white rounded-lg p-6 w-[320px] shadow-xl">
          {/* Triangle centré au-dessus de la modal */}
          <div
            className="absolute -top-2 w-4 h-4 bg-white transform rotate-45"
            style={{
              left: `${
                anchorEl
                  ? Math.min(
                      Math.max(anchorEl.getBoundingClientRect().left + anchorEl.offsetWidth / 2 - position.left - 8, 24),
                      296 // 320 - 24 pour garder le triangle dans les limites de la modal
                    )
                  : 160
              }px`, // 160px est le milieu de la modal (320/2)
            }}
          />

          <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-500">
            <HiOutlineX className="w-4 h-4" />
          </button>

          <div className="text-center">
            <h1 className="text-lg font-bold">Confirmer que c'est vous</h1>
            <p className="text-sm mt-2">
              Entrer le code reçu par {otpType} au {otpData} pour préremplir votre demande de manière sécurisée
              <br />
              {otpType === "email" && <span className="text-xs text-red-500"> (L'email peut être dans vos spams)</span>}
            </p>

            {isLoading ? (
              <div className="flex gap-2 justify-center mt-4">
                <p>Chargement...</p>
              </div>
            ) : (
              <div className="flex gap-2 justify-center mt-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    autoFocus={index === 0}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    ref={(el) => (inputRefs.current[index] = el)}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    className="w-8 h-8 text-center border border-gray-300 rounded-lg text-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                ))}
              </div>
            )}
            <p className="text-xs mt-4">Vous pourrez vérifier vos informations à la prochaine étape</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AgeRestrictionModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm transition-opacity duration-300" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full mx-auto shadow-2xl transition-all duration-300 opacity-100 scale-100">
          <div className="relative p-6">
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Fermer">
              <HiOutlineX className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <div className="bg-red-100 rounded-full p-3 h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <HiOutlineExclamationCircle className="h-10 w-10 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Ce service est uniquement accessible aux personnes majeures.</h2>
              <div className="h-1 w-16 bg-red-500 mx-auto mt-2 rounded-full"></div>
            </div>

            <div className="mb-6 text-center">
              <p className="text-gray-600">Veuillez vous présenter directement à l'accueil du laboratoire avec votre ordonnance.</p>
            </div>

            <div className="flex justify-center">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-all duration-200 hover:scale-105 shadow-md"
              >
                J'ai compris
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Identification;
